import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const countUp = (start, end, duration, setValue) => {
  let startTime = null;

  const animate = (timestamp) => {
    if (!startTime) startTime = timestamp;
    const progress = Math.min((timestamp - startTime) / duration, 1);
    const value = Math.floor(progress * (end - start) + start);
    setValue(value);
    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };

  requestAnimationFrame(animate);
};

const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [rating, setRating] = useState(0);
  const [customers, setCustomers] = useState(0);
  const [satisfaction, setSatisfaction] = useState(0);
  const [experience, setExperience] = useState(0);
  const statsRef = React.useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  


  const testimonials = [
  {
    id: 1,
    name: "Budi Santoso",
    role: "Pengusaha",
    location: "Pamulang, Tangerang Selatan",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15c31de92-1763296077723.png",
    avatarAlt: "Professional headshot of Indonesian businessman Budi Santoso wearing navy blue batik shirt with warm smile",
    rating: 5,
    date: "15 Desember 2025",
    motor: "Honda PCX 160",
    review: "Pelayanan di Honda Pamulang sangat memuaskan! Sales yang ramah dan profesional membantu saya memilih motor yang tepat. Proses kredit juga cepat dan mudah. Sangat recommended untuk yang mencari dealer Honda terpercaya di area Pamulang.",
    verified: true
  },
  {
    id: 2,
    name: "Siti Nurhaliza",
    role: "Ibu Rumah Tangga",
    location: "Ciputat, Tangerang Selatan",
    avatar: "https://images.unsplash.com/photo-1636004116224-82540f70d0a5",
    avatarAlt: "Friendly portrait of Indonesian housewife Siti Nurhaliza wearing pink hijab with genuine smile and warm expression",
    rating: 5,
    date: "8 Desember 2025",
    motor: "Honda BeAT Street",
    review: "Saya sangat puas dengan Honda BeAT yang saya beli di sini. Motor irit BBM dan cocok untuk kebutuhan sehari-hari. Tim service juga sangat membantu dan menjelaskan perawatan motor dengan detail. Terima kasih Honda Pamulang!",
    verified: true
  },
  {
    id: 3,
    name: "Ahmad Rizki",
    role: "Karyawan Swasta",
    location: "Serpong, Tangerang Selatan",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_154d703dc-1763301115517.png",
    avatarAlt: "Professional photo of young Indonesian employee Ahmad Rizki in white shirt with confident smile in office setting",
    rating: 5,
    date: "2 Desember 2025",
    motor: "Honda Vario 160",
    review: "Pengalaman beli motor di Honda Pamulang luar biasa! Dari awal konsultasi sampai serah terima motor, semuanya lancar. Bonus aksesoris yang diberikan juga menarik. Pokoknya top deh pelayanannya!",
    verified: true
  },
  {
    id: 4,
    name: "Dewi Lestari",
    role: "Mahasiswa",
    location: "Bintaro, Tangerang Selatan",
    avatar: "https://images.unsplash.com/photo-1618508792593-35aa016192bb",
    avatarAlt: "Cheerful portrait of Indonesian college student Dewi Lestari wearing casual denim jacket with bright smile outdoors",
    rating: 5,
    date: "25 November 2025",
    motor: "Honda PCX 160",
    review: "Sebagai pembeli pertama kali, saya sangat terbantu dengan penjelasan detail dari tim Honda Pamulang. Mereka sabar menjawab semua pertanyaan saya dan membantu proses kredit untuk mahasiswa. Sangat puas dengan pelayanannya!",
    verified: true
  },
  {
    id: 5,
    name: "Eko Prasetyo",
    role: "Wiraswasta",
    location: "Pondok Aren, Tangerang Selatan",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_119e2da1c-1763293740922.png",
    avatarAlt: "Confident portrait of Indonesian entrepreneur Eko Prasetyo wearing black polo shirt with professional demeanor",
    rating: 5,
    date: "18 November 2025",
    motor: "Honda ADV 160",
    review: "Honda ADV 160 yang saya beli sangat cocok untuk touring. Dealer Honda Pamulang memberikan service after sales yang excellent. Booking service online juga mudah dan praktis. Highly recommended!",
    verified: true
  }];


  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials?.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials?.length]);

  useEffect(() => {
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting && !hasAnimated) {
        setHasAnimated(true);

        countUp(0, 4.9, 1200, setRating);
        countUp(0, 50, 1500, setCustomers);
        countUp(0, 98, 1500, setSatisfaction);
        countUp(0, 15, 1200, setExperience);
      }
    },
    { threshold: 0.3 }
  );

  if (statsRef.current) observer.observe(statsRef.current);

  return () => observer.disconnect();
}, [hasAnimated]);


  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials?.length) % testimonials?.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 15000);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials?.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 15000);
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 15000);
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="MessageSquare" size={18} color="var(--color-primary)" />
            <span className="text-sm font-heading font-semibold text-primary" data-aos="fade-up" data-aos-delay="100">
              Testimoni Pelanggan
            </span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4" data-aos="fade-up" data-aos-delay="100">
            Apa Kata Pelanggan Kami
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto" data-aos="fade-up" data-aos-delay="100">
            Kepuasan pelanggan adalah prioritas utama kami. Simak pengalaman mereka bersama Honda Pamulang
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          <div className="bg-card rounded-2xl shadow-card-hover p-6 md:p-8 lg:p-12">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="relative">
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden ring-4 ring-primary/20">
                    <Image
                      src={testimonials?.[currentIndex]?.avatar}
                      alt={testimonials?.[currentIndex]?.avatarAlt}
                      className="w-full h-full object-cover" />

                  </div>
                  {testimonials?.[currentIndex]?.verified &&
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-lg">
                      <Icon name="CheckCircle" size={20} color="white" />
                    </div>
                  }
                </div>
              </div>

              <div className="flex-1 space-y-4">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-1">
                      {testimonials?.[currentIndex]?.name}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-1">
                      {testimonials?.[currentIndex]?.role}
                    </p>
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                      <Icon name="MapPin" size={14} />
                      <span>{testimonials?.[currentIndex]?.location}</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end">
                    <div className="flex items-center space-x-1 mb-2">
                      {[...Array(testimonials?.[currentIndex]?.rating)]?.map((_, i) =>
                      <Icon key={i} name="Star" size={18} color="var(--color-warning)" className="fill-warning" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {testimonials?.[currentIndex]?.date}
                    </p>
                  </div>
                </div>

                <div className="inline-flex items-center space-x-2 px-3 py-1 bg-primary/10 rounded-full">
                  <Icon name="Bike" size={14} color="var(--color-primary)" />
                  <span className="text-xs font-heading font-semibold text-primary">
                    {testimonials?.[currentIndex]?.motor}
                  </span>
                </div>

                <div className="relative">
                  <Icon name="Quote" size={40} color="var(--color-primary)" className="absolute -top-2 -left-2 opacity-20" />
                  <p className="text-base md:text-lg text-foreground leading-relaxed relative z-10 pl-6">
                    {testimonials?.[currentIndex]?.review}
                  </p>
                </div>

                {testimonials?.[currentIndex]?.verified &&
                <div className="flex items-center space-x-2 text-sm text-success">
                    <Icon name="ShieldCheck" size={16} />
                    <span className="font-medium">Pembeli Terverifikasi</span>
                  </div>
                }
              </div>
            </div>
          </div>

          <button
            onClick={handlePrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 w-12 h-12 bg-card hover:bg-primary rounded-full flex items-center justify-center shadow-lg transition-all duration-300 group"
            aria-label="Previous testimonial">

            <Icon name="ChevronLeft" size={24} className="text-foreground group-hover:text-primary-foreground transition-colors" />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 w-12 h-12 bg-card hover:bg-primary rounded-full flex items-center justify-center shadow-lg transition-all duration-300 group"
            aria-label="Next testimonial">

            <Icon name="ChevronRight" size={24} className="text-foreground group-hover:text-primary-foreground transition-colors" />
          </button>

          <div className="flex justify-center space-x-2 mt-8">
            {testimonials?.map((_, index) =>
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'w-8 bg-primary' : 'w-2 bg-muted hover:bg-primary/50'}`
              }
              aria-label={`Go to testimonial ${index + 1}`} />

            )}
          </div>
        </div>

        <div ref={statsRef}className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 md:mt-16">
          <div className="text-center p-6 bg-card rounded-xl">
            <div className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2">{rating.toFixed(1)}</div>
            <div className="flex items-center justify-center space-x-1 mb-2">
              {[...Array(5)]?.map((_, i) =>
              <Icon key={i} name="Star" size={16} color="var(--color-warning)" className="fill-warning" />
              )}
            </div>
            <div className="text-sm text-muted-foreground">Rating Google</div>
          </div>

          <div className="text-center p-6 bg-card rounded-xl">
            <div className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2">{customers}K+</div>
            <div className="text-sm text-muted-foreground">Pelanggan Puas</div>
          </div>

          <div className="text-center p-6 bg-card rounded-xl">
            <div className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2">{satisfaction}%</div>
            <div className="text-sm text-muted-foreground">Tingkat Kepuasan</div>
          </div>

          <div className="text-center p-6 bg-card rounded-xl">
            <div className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2">{experience}+</div>
            <div className="text-sm text-muted-foreground">Tahun Pengalaman</div>
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialCarousel;